import {
	ConnectionHandler,
	getFragment,
	type ReaderSelection,
	type IEnvironment,
	type RecordProxy,
	type RecordSourceSelectorProxy,
	type RecordSourceProxy,
	type Disposable,
} from 'relay-runtime';

import RelayDataID from '@atlassian/relay-data-id';

import type { AgentSessionEvent } from './agentSessionEventSource';
import { agentSessionSnapshot } from './agentSessionSnapshot';
import { UNSEEN_WINDOW_MS } from './agentSessionsPolicy';
import {
	getAgentSessionsScopeKey,
	type ResolvedAgentSessionsScope,
} from './AgentSessionsScopeProvider';

type Store = RecordSourceProxy | RecordSourceSelectorProxy;
export type ConnectionPolicy = Readonly<{
	key: string;
	filters: Readonly<Record<string, string>>;
	extent: number;
	unseen: boolean;
	includeLocal: boolean;
}>;

export const isKnownUnseen = (
	node: { hasUnseenActivity?: boolean | null; lastStateChangeAt?: number | null },
	now: number,
): boolean =>
	node.hasUnseenActivity === true &&
	typeof node.lastStateChangeAt === 'number' &&
	Number.isFinite(node.lastStateChangeAt) &&
	node.lastStateChangeAt <= now &&
	node.lastStateChangeAt >= now - UNSEEN_WINDOW_MS;

// Read the transaction's records, including fields just normalized by an overlapping query.
// A selected null is complete; only missing selected fields require hydration.
const hasSelections = (node: RecordProxy, selections: readonly ReaderSelection[]): boolean =>
	selections.every((selection) => {
		if (selection.kind === 'ScalarField') return node.getValue(selection.name) !== undefined;
		if (selection.kind === 'LinkedField') {
			if (selection.plural) {
				const records = node.getLinkedRecords(selection.name);
				return (
					records !== undefined &&
					(records === null ||
						records.every(
							(record) =>
								record === null ||
								(record !== undefined && hasSelections(record, selection.selections)),
						))
				);
			}
			const record = node.getLinkedRecord(selection.name);
			return (
				record !== undefined && (record === null || hasSelections(record, selection.selections))
			);
		}
		if (selection.kind === 'InlineFragment')
			return selection.type !== node.getType() || hasSelections(node, selection.selections);
		return false;
	});

export const hasAgentSessionDisplay = (store: Store, sessionId: string): boolean => {
	const node = store.get(RelayDataID({ id: sessionId }, 'AgentSession')!);
	return Boolean(node && hasSelections(node, getFragment(agentSessionSnapshot).selections));
};

export const reconcileAgentSessionConnection = (
	store: Store,
	policy: ConnectionPolicy,
	sessionId: string,
	remove: boolean,
	canInsert: boolean,
	now: number,
): void => {
	const connection = ConnectionHandler.getConnection(store.getRoot(), policy.key, policy.filters);
	if (!connection) return;
	const node = store.get(RelayDataID({ id: sessionId }, 'AgentSession')!);
	const edges = connection.getLinkedRecords('edges') ?? [];
	const existing = edges.find(
		(edge) => edge?.getLinkedRecord('node')?.getValue('id') === sessionId,
	);
	const dataID = node?.getDataID() ?? existing?.getLinkedRecord('node')?.getDataID();
	if (!dataID) return;
	const unseen =
		node &&
		isKnownUnseen(
			{
				hasUnseenActivity: node.getValue('hasUnseenActivity') as boolean | null | undefined,
				lastStateChangeAt: node.getValue('lastStateChangeAt') as number | null | undefined,
			},
			now,
		);
	if (
		remove ||
		!node ||
		(!policy.includeLocal && node.getValue('invocationType') === 'LOCAL_AGENT_SESSION') ||
		(policy.unseen && !unseen)
	) {
		ConnectionHandler.deleteNode(connection, dataID);
		return;
	}
	if (
		!existing &&
		(!canInsert || (!policy.includeLocal && node.getValue('invocationType') == null))
	)
		return;
	let edge = existing;
	if (!edge) {
		edge = ConnectionHandler.createEdge(store, connection, node, 'AgentSessionEdge');
		edge.setValue(null, 'cursor');
	}
	const ordered = edges.filter(
		(item): item is RecordProxy =>
			item != null && item.getLinkedRecord('node')?.getDataID() !== dataID,
	);
	const timestamp = node.getValue('lastStateChangeAt');
	if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return;
	// Existing ties keep their position; a new tie follows the already-known rows.
	const oldIndex = existing ? edges.indexOf(existing) : Number.MAX_SAFE_INTEGER;
	let index = ordered.findIndex((item, i) => {
		const other = item.getLinkedRecord('node')?.getValue('lastStateChangeAt');
		return typeof other !== 'number' || other < timestamp || (other === timestamp && i >= oldIndex);
	});
	if (index < 0) index = ordered.length;
	ordered.splice(index, 0, edge);
	connection.setLinkedRecords(ordered.slice(0, policy.extent), 'edges');
};

const writeEvent = (store: Store, event: AgentSessionEvent) => {
	if (!event.session) return;
	const id = RelayDataID({ id: event.sessionId }, 'AgentSession')!;
	const node = store.get(id) ?? store.create(id, 'AgentSession');
	const { conversationChannel } = event.session;
	for (const key of [
		'id',
		'state',
		'stateReason',
		'expireTime',
		'lastStateChangeAt',
		'isRovo',
		'agentAccountId',
		'convoAiAgentId',
		'hasUnseenActivity',
		'invocationType',
	] as const) {
		const value = event.session[key];
		if (value !== undefined) node.setValue(value, key);
	}
	if (conversationChannel === null) node.setValue(null, 'nativeConversationChannel');
	else if (conversationChannel) {
		const channelID = conversationChannel.id
			? RelayDataID({ id: conversationChannel.id }, 'AgentConversationChannel')!
			: (node.getLinkedRecord('nativeConversationChannel')?.getDataID() ??
				`${id}:nativeConversationChannel`);
		const channel = store.get(channelID) ?? store.create(channelID, 'AgentConversationChannel');
		for (const [key, value] of Object.entries(conversationChannel)) {
			if (
				value !== undefined &&
				(value === null || typeof value !== 'object' || Array.isArray(value))
			)
				channel.setValue(value, key);
		}
		node.setLinkedRecord(channel, 'nativeConversationChannel');
	}
};

/** Temporary event precedence while older query operations publish into Relay. */
export class AgentSessionReconciliation {
	private operations = new Set<symbol>();
	private journal = new Map<string, AgentSessionEvent>();
	private latest = new Map<string, { sequence: number; removed: boolean }>();
	private deleted = new Map<string, AgentSessionEvent>();
	private handlers = new Set<(store: Store, event: AgentSessionEvent) => void>();
	private overlay: Disposable | undefined;
	constructor(private environment: IEnvironment) {}
	addHandler(handler: (store: Store, event: AgentSessionEvent) => void): Disposable {
		this.handlers.add(handler);
		return {
			dispose: () => {
				this.handlers.delete(handler);
			},
		};
	}
	current(sessionId: string): { sequence: number; removed: boolean } | undefined {
		return this.latest.get(sessionId);
	}
	private apply = (events: readonly AgentSessionEvent[]) => (store: Store) => {
		for (const event of events) {
			writeEvent(store, event);
			this.handlers.forEach((handler) => handler(store, event));
			if (event.session === null) {
				const id = RelayDataID({ id: event.sessionId }, 'AgentSession')!;
				const channel = store.get(id)?.getLinkedRecord('nativeConversationChannel');
				if (channel) store.delete(channel.getDataID());
				store.delete(RelayDataID({ id: event.sessionId }, 'AgentConversationChannel')!);
				store.delete(id);
			}
		}
	};
	event(event: AgentSessionEvent): void {
		const deleted = this.deleted.get(event.sessionId);
		if (deleted) {
			this.environment.commitUpdate(this.apply([deleted]));
			return;
		}
		if (event.session === null) this.deleted.set(event.sessionId, event);
		if ((this.latest.get(event.sessionId)?.sequence ?? -1) >= event.sequence) return;
		this.latest.set(event.sessionId, { sequence: event.sequence, removed: event.session === null });
		this.environment.commitUpdate(this.apply([event]));
		if (this.operations.size) {
			this.journal.set(event.sessionId, event);
			this.refreshOverlay();
		}
	}
	private refreshOverlay(): void {
		const previous = this.overlay;
		this.overlay = this.environment.applyUpdate({
			storeUpdater: this.apply([...this.journal.values()]),
		});
		previous?.dispose();
	}
	begin(seed?: AgentSessionEvent): Disposable {
		const operation = Symbol('agent-session-operation');
		this.operations.add(operation);
		if (this.deleted.size) {
			for (const [id, event] of this.deleted) this.journal.set(id, event);
			this.refreshOverlay();
		}
		// Hydration starts because of this event, so it must not overwrite that event's fields.
		if (seed && this.latest.get(seed.sessionId)?.sequence === seed.sequence) {
			this.journal.set(seed.sessionId, seed);
			this.refreshOverlay();
		}
		return {
			dispose: () => {
				if (!this.operations.delete(operation) || this.operations.size) return;
				if (this.journal.size)
					this.environment.commitUpdate(this.apply([...this.journal.values()]));
				this.overlay?.dispose();
				this.overlay = undefined;
				this.journal.clear();
			},
		};
	}
}

const environments = new WeakMap<IEnvironment, Map<string, AgentSessionReconciliation>>();
export const getAgentSessionReconciliation = (
	environment: IEnvironment,
	scope: ResolvedAgentSessionsScope,
): AgentSessionReconciliation => {
	let scopes = environments.get(environment);
	if (!scopes) {
		scopes = new Map();
		environments.set(environment, scopes);
	}
	const key = getAgentSessionsScopeKey(scope);
	let resource = scopes.get(key);
	if (!resource) {
		resource = new AgentSessionReconciliation(environment);
		scopes.set(key, resource);
	}
	return resource;
};
